import type { AraConfigurePayload } from '@/features/ara-generation/types/ara-configure'
import type { AraVariant } from '@/features/ara-generation/types/ara-workspace'
import { stripEditChrome } from '@/features/ara-generation/utils/ara-section-html'

/**
 * The editable ARA source persisted server-side (ADR 0001): structuredAra plus
 * section HTML, with the ordering/custom-section metadata needed to rehydrate
 * the editor on any browser. Versioned so future envelope changes are
 * detectable on read.
 */
export type AraSourcePayload = {
  version: 1
  structuredAra: Record<string, unknown>
  sections: Record<string, string>
  sectionOrder: string[]
  customSections: AraConfigurePayload['customSections']
  lastRenderedHtml: string | null
  /**
   * The variant's configure payload, scope selection included (SCP-129).
   * Optional so sources written before the field decode unchanged (they read
   * as "no configure" and the scope panel falls back to all-included).
   */
  configure?: AraConfigurePayload | null
  scopeSavedAt?: number | null
}

const ARA_SOURCE_VERSION = 1 as const

function cloneConfigureForSource(configure: AraConfigurePayload): AraConfigurePayload {
  return {
    ...configure,
    sections: configure.sections ? [...configure.sections] : null,
    sectionOrder: [...configure.sectionOrder],
    customSections: configure.customSections.map(section => ({ ...section })),
    sectionGuidanceExtra: configure.sectionGuidanceExtra
      ? { ...configure.sectionGuidanceExtra }
      : null,
    includedAssetIds: configure.includedAssetIds ? [...configure.includedAssetIds] : null,
    attachments: configure.attachments.map(attachment => ({ ...attachment }))
  }
}

/**
 * Builds the source payload from a variant, preferring the live edit state
 * (superset of the generation-time draft payload once editing starts).
 */
export function buildAraSourcePayload(variant: AraVariant): AraSourcePayload | null {
  const draft = variant.draftPayload
  if (!draft) return null
  const edit = variant.edit
  const rawSections = edit?.sections ?? draft.sections
  const sections: Record<string, string> = {}
  for (const [key, html] of Object.entries(rawSections)) {
    sections[key] = stripEditChrome(html)
  }
  return {
    version: ARA_SOURCE_VERSION,
    structuredAra: edit?.structuredAra ?? draft.structuredAra,
    sections,
    sectionOrder: edit?.sectionOrder ?? draft.sectionOrder,
    customSections: draft.customSections.map(section => ({ ...section })),
    lastRenderedHtml: edit?.lastRenderedHtml ?? draft.lastRenderedHtml ?? null,
    configure: variant.configure ? cloneConfigureForSource(variant.configure) : null,
    scopeSavedAt: variant.scopeSavedAt ?? null
  }
}

/**
 * Encodes the payload as base64 for the source endpoints: UTF-8 safe and
 * synchronous (chunked, so hundreds of KB don't blow arg limits) — the idle
 * batch awaits no FileReader macrotasks, which keeps sync fully inside the
 * batch it started in.
 */
export function encodeAraSource(payload: AraSourcePayload): string {
  const bytes = new TextEncoder().encode(JSON.stringify(payload))
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

function base64ToBytes(contentBase64: string): Uint8Array {
  const binary = atob(contentBase64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

/**
 * Decodes a downloaded source blob back into the payload, rejecting shapes
 * the editor cannot rehydrate from (wrong envelope, missing collections).
 * Parse failures surface to the caller as errors, never as partial state.
 */
export function decodeAraSource(contentBase64: string): AraSourcePayload {
  const parsed: unknown = JSON.parse(new TextDecoder().decode(base64ToBytes(contentBase64)))
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('ARA source is not an object')
  }
  const candidate = parsed as Partial<AraSourcePayload>
  if (candidate.version !== 1) {
    throw new Error('unsupported ARA source version')
  }
  if (!candidate.sections || typeof candidate.sections !== 'object') {
    throw new Error('ARA source is missing sections')
  }
  if (!candidate.structuredAra || typeof candidate.structuredAra !== 'object') {
    throw new Error('ARA source is missing structuredAra')
  }
  if (!Array.isArray(candidate.sectionOrder)) {
    throw new Error('ARA source is missing sectionOrder')
  }
  // Light shape guard: a malformed configure must never reach the regeneration
  // request — it degrades to "no configure" (scope panel falls back to all).
  const configure =
    candidate.configure &&
    typeof candidate.configure === 'object' &&
    Array.isArray((candidate.configure as AraConfigurePayload).sectionOrder)
      ? (candidate.configure as AraConfigurePayload)
      : null
  return {
    version: 1,
    structuredAra: candidate.structuredAra,
    sections: candidate.sections,
    sectionOrder: candidate.sectionOrder,
    customSections: candidate.customSections ?? [],
    lastRenderedHtml: candidate.lastRenderedHtml ?? null,
    configure,
    scopeSavedAt: typeof candidate.scopeSavedAt === 'number' ? candidate.scopeSavedAt : null
  }
}
